import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/errors';

interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateWorkspaceModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateWorkspaceModalProps) {
  const [name, setName] = useState('');
  const { refetchWorkspaces, setActiveWorkspace } = useWorkspaceScope();

  const createWorkspace = useMutation({
    mutationFn: async (workspaceName: string) => {
      const { data, error } = await supabase.rpc('create_workspace_with_limit', {
        p_name: workspaceName || 'My Workspace',
      });

      if (error) {
        // Check if it's a workspace limit error
        if (error.message?.includes('WORKSPACE_LIMIT_REACHED') || error.code === 'P0001') {
          throw new Error('WORKSPACE_LIMIT_REACHED');
        }
        throw error;
      }

      if (!data || !(data as any).success) {
        const errorMsg = (data as any)?.error_message || 'Failed to create workspace';
        throw new Error(errorMsg);
      }

      return data as { success: boolean; workspace_id: string; name: string };
    },
    onSuccess: async (data) => {
      toast.success(`Workspace "${data.name}" created`);
      setName('');
      onOpenChange(false);
      
      // Refetch workspaces and switch to new one
      await refetchWorkspaces();
      if (data.workspace_id) {
        setActiveWorkspace(data.workspace_id);
      }
      
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const msg = errorMessage(error, 'Failed to create workspace');
      if (msg === 'WORKSPACE_LIMIT_REACHED') {
        toast.error('Workspace limit reached. Upgrade to Pro to create more workspaces.');
      } else {
        toast.error(msg);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      createWorkspace.mutate(name.trim());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Workspace</DialogTitle>
          <DialogDescription>
            Create a new workspace to organize your leads and deals separately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace Name</Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Workspace"
              autoFocus
              disabled={createWorkspace.isPending}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setName('');
                onOpenChange(false);
              }}
              disabled={createWorkspace.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createWorkspace.isPending || !name.trim()}
            >
              {createWorkspace.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Create
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

